import React                     from 'react';
import { observer }              from 'mobx-react-lite';
import { Shape }                 from '@tokamap/core';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import Spin                      from '@widesk-ui/components/Spin';
import Alert                     from '@widesk-ui/components/Alert';
import { EnvironmentOutlined }   from '@ant-design/icons';
import { FormatPainterOutlined } from '@ant-design/icons';
import { LeftOutlined }          from '@ant-design/icons';
import { RightOutlined }         from '@ant-design/icons';
import Tooltip                   from '@widesk-ui/components/Tooltip';
import { DisconnectOutlined }    from '@ant-design/icons';
import PlaceModel                from '@models/map/PlaceModel';
import PlaceForm                 from '@forms/map/PlaceForm';
import { PlaceTitle }            from '@forms/map/PlaceForm';
import { PlaceHeaderImage }      from '@forms/map/PlaceForm';
import useDesignTokens           from '@/hooks/useDesignTokens';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import AreaSidebar               from '@/TokamapEditor/ux/AreaSidebar/AreaSidebar';
import SidebarTabs               from '@/TokamapEditor/ux/AreaSidebar/SidebarTabs';
import placeShapesCount          from '@/TokamapEditor/helpers/placeShapesCount';
import shapeMeasure              from '@/TokamapEditor/helpers/shapeMeasure';
import { SHOW_SHAPE_MEASURES }   from '@/TokamapEditor/ux/constants';
import placePanelStore           from '@/TokamapEditor/stores/placePanelStore';
import { PlacePanelLevel }       from '@/TokamapEditor/stores/placePanelStore';
import unlinkPlaceContent        from '@/TokamapEditor/helpers/unlinkPlaceContent';
import asSingleHistoryEntry      from '@/TokamapEditor/helpers/asSingleHistoryEntry';

const MemoPlaceForm = React.memo(PlaceForm);

const PlaceShapePanel = observer(({ shape, placeId }: { shape: Shape; placeId: string; }) => {
	const level = placePanelStore.level;
	const [place, setPlace] = React.useState<PlaceModel>();
	const [missing, setMissing] = React.useState(false);
	// Le panneau survit au passage d'une forme du point d'intérêt à une autre (voir `ShapeSidebar`) : ce
	// qui est posé une fois dans l'en-tête relit la forme courante plutôt que celle du montage.
	const shapeRef = React.useRef(shape);
	shapeRef.current = shape;

	React.useEffect(() => {
		let cancelled = false;
		const model = new PlaceModel();

		model.setId(placeId).fetch()
			.then(() => {
				if (cancelled) return;

				leftSidebarStore.setPanelOption(0, 'parentTitle', PlaceModel.localize());
				leftSidebarStore.setPanelOption(0, 'title', <PlaceTitle model={model} />);
				leftSidebarStore.setPanelOption(0, 'renderTopLeft', () => <PlaceHeaderImage model={model} size={headerImageSize()} />);
				leftSidebarStore.setPanelOption(0, 'bordered', false);
				leftSidebarStore.setPanelOption(0, 'subTitle', <PlaceShapesBanner placeId={placeId} />);
				setPlace(model);
			})
			.catch(err => {
				if (cancelled) return;

				console.error(`${PlaceModel.localize()} de la forme`, err);
				leftSidebarStore.setPanelOption(0, 'title', `${PlaceModel.localize()} introuvable`);
				setMissing(true);
			});

		return () => {
			cancelled = true;
		};
	}, [placeId]);

	// Grandit avec les lignes que le compteur de formes et la mesure ajoutent sous le nom, pour les couvrir.
	const headerImageSize = React.useCallback(() => {
		const selected = currentEditorStore.selectedShape;
		const lines = Number(placeShapesCount(placeId).shown) + Number(!!selected && !!shapeDetails(selected));

		return [undefined, 60, 72][lines];
	}, [placeId]);

	// Stables d'un rendu à l'autre (la forme courante est lue dans `shapeRef`) : la fiche est mémoïsée,
	// sans quoi chaque cran d'un curseur de « Mise en forme » la recalculerait en entier.
	const headerActions = React.useMemo(() => [{
		icon: <DisconnectOutlined />,
		label: `Dissocier du ${PlaceModel.localize('default', 'l')}`,
		onClick: () => asSingleHistoryEntry(async () => {
			const editor = currentEditorStore.get;
			const shape = shapeRef.current;

			await unlinkPlaceContent([shape]);

			editor.setShapeEntity([shape.id], undefined);
			editor.setShapeAction([shape.id], undefined);
		}),
	}], []);

	const placeContent = (
		<>
			{missing && (
				<View p="md">
					<Alert
						type="warning"
						showIcon
						message={`${PlaceModel.localize('the')} associé à cette forme n'existe plus.`}
					/>
				</View>
			)}

			{!missing && !place && (
				<View center py={64}>
					<Spin />
				</View>
			)}

			{!!place && (
				<View paddingH="lg" paddingV="md">
					<MemoPlaceForm
						inSidebar
						model={place}
						projectUrn={currentProjectEditorStore.get.project.urn}
						headerImageSize={headerImageSize}
						headerActions={headerActions}
					/>
				</View>
			)}
		</>
	);

	const changeLevel = (value: PlacePanelLevel) => placePanelStore.setLevel(value);

	return (
		<View heightF style={{ minHeight: 0 }}>
			<SidebarTabs
				large
				value={level}
				onChange={changeLevel}
				options={[
					{ value: 'place', label: `Fiche du ${PlaceModel.localize('default', 'l')}`, icon: <EnvironmentOutlined /> },
					{
						value: 'style',
						label: 'Mise en forme',
						icon: <FormatPainterOutlined />,
						title: 'Ces réglages ne concernent que cette forme.',
					},
				]}
			/>

			<View flex className="sidebar-scroll" style={{ minHeight: 0, overflowY: 'auto', display: level === 'place' ? undefined : 'none' }}>
				{placeContent}
			</View>

			{level === 'style' && (
				<View flex style={{ minHeight: 0 }}>
					<AreaSidebar embedded />
				</View>
			)}
		</View>
	);
});

const PlaceShapesBanner = observer(({ placeId }: { placeId: string; }) => {
	const tokens = useDesignTokens();
	const { here, elsewhere, shown } = placeShapesCount(placeId);
	const selected = currentEditorStore.selectedShape;
	const index = here.findIndex(s => s.id === selected?.id);
	const details = selected ? shapeDetails(selected) : undefined;

	const select = (step: 1 | -1) => {
		const target = here[(index + step + here.length) % here.length];

		if (target) currentEditorStore.requestShapeSelection(target.id);
	};

	if (!shown && !details) return null;

	const arrow = (step: 1 | -1) => (
		<Tooltip title={step > 0 ? 'Forme suivante' : 'Forme précédente'}>
			<Button
				type="text"
				size="small"
				icon={step > 0 ? <RightOutlined /> : <LeftOutlined />}
				onClick={() => select(step)}
				style={{ width: 24, height: 24, minWidth: 24, color: tokens.text.default, fontSize: 11 }}
			/>
		</Tooltip>
	);

	return (
		// Sous le nom, dans l'en-tête : calque et mesure, puis le compteur de formes. Cette ligne, plus
		// basse que la rangée du compteur et ses flèches, collerait au nom sans sa marge.
		<View gap={2} size={12.5} color={tokens.text.muted} style={{ fontWeight: 400, marginTop: details ? 4 : undefined }}>
			{!!details && <View style={{ fontVariantNumeric: 'tabular-nums' }}>{details}</View>}

			{/* La flèche de gauche recule de sa marge intérieure pour que son chevron s'aligne sur le nom. */}
			{shown && (
				<View row centerV gap={4} wrap style={{ marginLeft: here.length > 1 ? -7 : 0 }}>
					{here.length > 1 ? (
						<>
							{arrow(-1)}

							<span style={{ fontVariantNumeric: 'tabular-nums' }}>
								Forme <span style={{ color: tokens.text.default, fontWeight: 600 }}>{index + 1}</span> sur {here.length}
							</span>

							{arrow(1)}
						</>
					) : (
						<View>Seule forme sur cette carte</View>
					)}

					{!!elsewhere && <View style={{ marginLeft: 4 }}>· +{elsewhere} sur d'autres cartes</View>}
				</View>
			)}
		</View>
	);
});

/** Calque et mesure : l'en-tête de la fiche remplace celui de la forme, qui les affichait. */
function shapeDetails(shape: Shape) {
	return [shape.layer !== undefined && shape.layer !== null ? `Calque ${shape.layer}` : undefined, SHOW_SHAPE_MEASURES ? shapeMeasure(shape, currentEditorStore.lengthUnit) : undefined]
		.filter(Boolean)
		.join(' · ') || undefined;
}

export default PlaceShapePanel;
